import * as bitcoin from 'bitcoinjs-lib';
import encHex from 'crypto-js/enc-hex';
import sha256 from 'crypto-js/sha256';
import * as ecc from '@bitcoinerlab/secp256k1';
import type { Amount } from '@cosmostation/extension-client/types/message';

import { DYDX_CHAIN } from 'src/constants/chain';
import { DYDX_TOKEN } from 'src/constants/tokens';
import { DYDX_LCD_URL } from 'src/constants/transaction';
import { cosmos, google } from 'src/proto/cosmos-v0.44.2.js';
import type {
  Msg,
  MsgDelegate,
  MsgRedelegate,
  MsgReward,
  MsgSend,
  MsgUndelegate,
  MsgVote,
  ProtoTxBytesProps,
  PubKey,
  SignAminoDoc,
  SignDirectDoc,
} from 'src/types/proto';
import type { SendTransactionPayload } from 'src/types/tx';

import { post } from './axios';
import { mnemonicToPair } from './crypto';

bitcoin.initEccLib(ecc);

const requesturl = `${DYDX_LCD_URL}/cosmos/tx/v1beta1/txs`;

export function protoTxBytes({ signature, txBodyBytes, authInfoBytes }: ProtoTxBytesProps) {
  const txRaw = new cosmos.tx.v1beta1.TxRaw({
    body_bytes: new Uint8Array(txBodyBytes),
    auth_info_bytes: new Uint8Array(authInfoBytes),
    signatures: [Buffer.from(signature, 'base64')],
  });
  const txRawBytes = cosmos.tx.v1beta1.TxRaw.encode(txRaw).finish();

  const tx = {
    tx_bytes: Buffer.from(txRawBytes).toString('base64'),
    mode: cosmos.tx.v1beta1.BroadcastMode.BROADCAST_MODE_SYNC,
  };

  return tx;
}

export function broadcast(url: string, body: unknown) {
  return post<SendTransactionPayload>(url, body);
}

export async function signDirectTx(doc: SignDirectDoc, keyPair: { privateKey: Buffer }, accountNumber: string) {
  const auth_info_bytes = new Uint8Array(doc.auth_info_bytes);
  const body_bytes = new Uint8Array(doc.body_bytes);
  const signedDoc = { chain_id: DYDX_CHAIN.chainId, account_number: accountNumber, body_bytes, auth_info_bytes };

  const signature = signDirect(signedDoc, keyPair.privateKey);

  const base64Signature = Buffer.from(signature).toString('base64');

  const pTxBytes = protoTxBytes({
    signature: base64Signature,
    txBodyBytes: signedDoc.body_bytes,
    authInfoBytes: signedDoc.auth_info_bytes,
  });

  const response = await broadcast(requesturl, pTxBytes);

  return response;
}

export function signDirect(signDoc: SignDirectDoc, privateKey: Buffer) {
  const txSignDoc = new cosmos.tx.v1beta1.SignDoc({
    ...signDoc,
    auth_info_bytes: new Uint8Array(signDoc.auth_info_bytes),
    body_bytes: new Uint8Array(signDoc.body_bytes),
    account_number: Number(signDoc.account_number),
  });

  const txSignDocHex = Buffer.from(cosmos.tx.v1beta1.SignDoc.encode(txSignDoc).finish()).toString('hex');

  const sha256SignDoc = (() => sha256(encHex.parse(txSignDocHex)).toString(encHex))();

  const signatureBuffer = ecc.sign(Buffer.from(sha256SignDoc, 'hex'), privateKey);

  return signatureBuffer;
}

type getDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  validatorAddress: string;
  delegateAmount: string;
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getDelegateDirectDoc(props?: getDirectDocProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgDelegate',
          value: {
            validator_address: props.validatorAddress,
            delegator_address: props.delegatorAddress,
            amount: { amount: props.delegateAmount, denom: DYDX_TOKEN.denom },
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getRedelegateDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  srcValidatorAddress: string;
  dstValidatorAddress: string;
  redelegateAmount: string;
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getReDelegateDirectDoc(props?: getRedelegateDirectDocProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgBeginRedelegate',
          value: {
            validator_src_address: props.srcValidatorAddress,
            validator_dst_address: props.dstValidatorAddress,
            delegator_address: props.delegatorAddress,
            amount: { amount: props.redelegateAmount, denom: DYDX_TOKEN.denom },
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getUndelegateDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  validatorAddress: string;
  undelegateAmount: string;
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getUndelegateDirectDoc(props?: getUndelegateDirectDocProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgUndelegate',
          value: {
            validator_address: props.validatorAddress,
            delegator_address: props.delegatorAddress,
            amount: { amount: props.undelegateAmount, denom: DYDX_TOKEN.denom },
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getWithdrawRewardDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  validatorAddress: string;
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getWithdrawRewardDirectDoc(props?: getWithdrawRewardDirectDocProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgWithdrawDelegationReward',
          value: {
            validator_address: props.validatorAddress,
            delegator_address: props.delegatorAddress,
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getWithdrawAllRewardDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  rewards: { validatorAddress: string; delegatorAddress: string }[];
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getWithdrawAllRewardDirectDoc(props?: getWithdrawAllRewardDirectDocProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: props.rewards.map((item) => ({
        type: 'cosmos-sdk/MsgWithdrawDelegationReward',
        value: { delegator_address: item.delegatorAddress, validator_address: item.validatorAddress },
      })),
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getVoteDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  voterAddress: string;
  proposalId: string;
  option: cosmos.gov.v1beta1.VoteOption;
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getVoteDirectDoc(props?: getVoteDirectDocProps) {
  if (props) {
    const simulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgVote',
          value: {
            proposal_id: Number(props.proposalId),
            voter: props.voterAddress,
            option: props.option,
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      simulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getSendDirectDocProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  senderAddress: string;
  receiverAddress: string;
  amount: Amount;
  simulatedGas: string;
  simulatedFeeAmount: string;
  memo?: string;
};

export function getSendDirectDoc(props?: getSendDirectDocProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: props.simulatedFeeAmount, denom: DYDX_TOKEN.denom }],
        gas: props.simulatedGas,
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgSend',
          value: {
            from_address: props.senderAddress,
            to_address: props.receiverAddress,
            amount: [{ amount: props.amount.amount, denom: props.amount.denom }],
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx
      ? {
          chain_id: DYDX_CHAIN.chainId,
          account_number: props.accountNumber,
          auth_info_bytes: [...Array.from(pTx.authInfoBytes)],
          body_bytes: [...Array.from(pTx.txBodyBytes)],
        }
      : undefined;
  }
  return undefined;
}

type getSimulateTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  validatorAddress: string;
  delegateAmount: string;
  memo?: string;
};

export function getSimulateTxBytes(props?: getSimulateTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgDelegate',
          value: {
            validator_address: props.validatorAddress,
            delegator_address: props.delegatorAddress,
            amount: { amount: props.delegateAmount, denom: DYDX_TOKEN.denom },
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

type getSimulateRedelegateTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  srcValidatorAddress: string;
  dstValidatorAddress: string;
  redelegateAmount: string;
  memo?: string;
};

export function getSimulateRedelegateTxBytes(props?: getSimulateRedelegateTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgBeginRedelegate',
          value: {
            validator_src_address: props.srcValidatorAddress,
            validator_dst_address: props.dstValidatorAddress,
            delegator_address: props.delegatorAddress,
            amount: { amount: props.redelegateAmount, denom: DYDX_TOKEN.denom },
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

type getSimulateUndelegateTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  validatorAddress: string;
  undelegateAmount: string;
  memo?: string;
};

export function getSimulateUndelegateTxBytes(props?: getSimulateUndelegateTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgUndelegate',
          value: {
            validator_address: props.validatorAddress,
            delegator_address: props.delegatorAddress,
            amount: { amount: props.undelegateAmount, denom: DYDX_TOKEN.denom },
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

type getSimulateWithdrawRewardTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  delegatorAddress: string;
  validatorAddress: string;
  memo?: string;
};

export function getSimulateWithdrawRewardTxBytes(props?: getSimulateWithdrawRewardTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgWithdrawDelegationReward',
          value: {
            validator_address: props.validatorAddress,
            delegator_address: props.delegatorAddress,
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

type getSimulateWithdrawAllRewardTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  rewards: { validatorAddress: string; delegatorAddress: string }[];
  memo?: string;
};

export function getSimulateWithdrawAllRewardTxBytes(props?: getSimulateWithdrawAllRewardTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: props.rewards.map((item) => ({
        type: 'cosmos-sdk/MsgWithdrawDelegationReward',
        value: { delegator_address: item.delegatorAddress, validator_address: item.validatorAddress },
      })),
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

type getSimulateVoteTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  voterAddress: string;
  proposalId: string;
  option: cosmos.gov.v1beta1.VoteOption;
  memo?: string;
};

export function getSimulateVoteTxBytes(props?: getSimulateVoteTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgVote',
          value: {
            proposal_id: Number(props.proposalId),
            voter: props.voterAddress,
            option: props.option,
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

type getSimulateSendTxBytesProps = {
  accountNumber: string;
  sequence: string;
  mnemonic: string;
  senderAddress: string;
  receiverAddress: string;
  amount: Amount;
  memo?: string;
};

export function getSimulateSendTxBytes(props?: getSimulateSendTxBytesProps) {
  if (props) {
    const commissionSimulatedAminoTx = {
      account_number: props.accountNumber,
      sequence: props.sequence,
      chain_id: DYDX_CHAIN.chainId,
      fee: {
        amount: [{ amount: '5000', denom: DYDX_TOKEN.denom }],
        gas: '200000',
      },
      msgs: [
        {
          type: 'cosmos-sdk/MsgSend',
          value: {
            from_address: props.senderAddress,
            to_address: props.receiverAddress,
            amount: [{ amount: props.amount.amount, denom: props.amount.denom }],
          },
        },
      ],
      memo: props.memo || '',
    };

    const keyPair = mnemonicToPair(props.mnemonic);

    const base64PublicKey = keyPair ? Buffer.from(keyPair.publicKey).toString('base64') : '';

    const pTx = protoTx(
      commissionSimulatedAminoTx,
      '',
      { type: 'tendermint/PubKeySecp256k1', value: base64PublicKey },
      cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_DIRECT,
    );

    return pTx ? protoTxBytes({ ...pTx }) : null;
  }
  return undefined;
}

export function protoTx(signed: SignAminoDoc, signature: string, pubKey: PubKey, mode = cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_LEGACY_AMINO_JSON) {
  const txBodyBytes = getTxBodyBytes(signed);

  if (txBodyBytes === null) {
    return null;
  }

  const authInfoBytes = getAuthInfoBytes(signed, pubKey, mode);

  return { signature, txBodyBytes, authInfoBytes };
}

export function getTxBodyBytes(signed: SignAminoDoc) {
  const messages = signed.msgs.map((msg) => convertAminoMessageToProto(msg)).filter((item) => item !== null) as google.protobuf.Any[];

  const txBody = new cosmos.tx.v1beta1.TxBody({
    messages,
    memo: signed.memo,
  });

  if (signed.msgs.length !== messages.length) {
    return null;
  }

  return cosmos.tx.v1beta1.TxBody.encode(txBody).finish();
}

export function getAuthInfoBytes(signed: SignAminoDoc, pubKey: PubKey, mode = cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_LEGACY_AMINO_JSON) {
  const signerInfo = getSignerInfo(signed, pubKey, mode);

  const fee = new cosmos.tx.v1beta1.Fee({
    amount: signed.fee.amount,
    gas_limit: Number(signed.fee.gas),
  });

  const authInfo = new cosmos.tx.v1beta1.AuthInfo({ signer_infos: [signerInfo], fee });

  return cosmos.tx.v1beta1.AuthInfo.encode(authInfo).finish();
}

export function getSignerInfo(signed: SignAminoDoc, pubKey: PubKey, mode = cosmos.tx.signing.v1beta1.SignMode.SIGN_MODE_LEGACY_AMINO_JSON) {
  const publicKey = getPubKey(pubKey);

  const typeURL = (() => {
    if (pubKey.type === 'ethermint/PubKeyEthSecp256k1') {
      return '/ethermint.crypto.v1.ethsecp256k1.PubKey';
    }

    if (pubKey.type === 'injective/PubKeyEthSecp256k1') {
      return '/injective.crypto.v1beta1.ethsecp256k1.PubKey';
    }

    return '/cosmos.crypto.secp256k1.PubKey';
  })();

  return new cosmos.tx.v1beta1.SignerInfo({
    public_key: new google.protobuf.Any({
      type_url: typeURL,
      value: cosmos.crypto.secp256k1.PubKey.encode(publicKey).finish(),
    }),
    mode_info: {
      single: {
        mode,
      },
    },
    sequence: Number(signed.sequence),
  });
}

export function getPubKey(pubKey: PubKey) {
  const bufferPubKey = Buffer.from(pubKey.value, 'base64');
  const publicKey = new cosmos.crypto.secp256k1.PubKey({ key: bufferPubKey });
  return publicKey;
}

export function convertAminoMessageToProto(msg: Msg) {
  if (isAminoDelegate(msg)) {
    return convertAminoDelegateMessageToProto(msg);
  }

  if (isAminoRedelegate(msg)) {
    return convertAminoRedelegateMessageToProto(msg);
  }

  if (isAminoUndelegate(msg)) {
    return convertAminoUndelegateMessageToProto(msg);
  }

  if (isAminoUndelegate(msg)) {
    return convertAminoUndelegateMessageToProto(msg);
  }

  if (isAminoWithdrawReward(msg)) {
    return convertAminoWithdrawRewardMessageToProto(msg);
  }

  if (isAminoSend(msg)) {
    return convertAminoSendMessageToProto(msg);
  }

  if (isAminoVote(msg)) {
    return convertAminoVoteMessageToProto(msg);
  }

  return null;
}

export function isAminoDelegate(msg: Msg): msg is Msg<MsgDelegate> {
  return msg.type === 'cosmos-sdk/MsgDelegate';
}

export function isAminoRedelegate(msg: Msg): msg is Msg<MsgRedelegate> {
  return msg.type === 'cosmos-sdk/MsgBeginRedelegate';
}

export function isAminoUndelegate(msg: Msg): msg is Msg<MsgUndelegate> {
  return msg.type === 'cosmos-sdk/MsgUndelegate';
}

export function isAminoWithdrawReward(msg: Msg): msg is Msg<MsgReward> {
  return msg.type === 'cosmos-sdk/MsgWithdrawDelegationReward';
}

export function isAminoVote(msg: Msg): msg is Msg<MsgVote> {
  return msg.type === 'cosmos-sdk/MsgVote';
}

export function isAminoSend(msg: Msg): msg is Msg<MsgSend> {
  return msg.type === 'cosmos-sdk/MsgSend';
}

export function convertAminoDelegateMessageToProto(msg: Msg<MsgDelegate>) {
  const message = new cosmos.staking.v1beta1.MsgDelegate({
    amount: msg.value.amount,
    delegator_address: msg.value.delegator_address,
    validator_address: msg.value.validator_address,
  });

  return new google.protobuf.Any({
    type_url: '/cosmos.staking.v1beta1.MsgDelegate',
    value: cosmos.staking.v1beta1.MsgDelegate.encode(message).finish(),
  });
}

export function convertAminoRedelegateMessageToProto(msg: Msg<MsgRedelegate>) {
  const message = new cosmos.staking.v1beta1.MsgBeginRedelegate({
    amount: msg.value.amount,
    delegator_address: msg.value.delegator_address,
    validator_src_address: msg.value.validator_src_address,
    validator_dst_address: msg.value.validator_dst_address,
  });

  return new google.protobuf.Any({
    type_url: '/cosmos.staking.v1beta1.MsgBeginRedelegate',
    value: cosmos.staking.v1beta1.MsgBeginRedelegate.encode(message).finish(),
  });
}

export function convertAminoUndelegateMessageToProto(msg: Msg<MsgUndelegate>) {
  const message = new cosmos.staking.v1beta1.MsgUndelegate({
    amount: msg.value.amount,
    delegator_address: msg.value.delegator_address,
    validator_address: msg.value.validator_address,
  });

  return new google.protobuf.Any({
    type_url: '/cosmos.staking.v1beta1.MsgUndelegate',
    value: cosmos.staking.v1beta1.MsgUndelegate.encode(message).finish(),
  });
}

export function convertAminoWithdrawRewardMessageToProto(msg: Msg<MsgReward>) {
  const message = new cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward({
    delegator_address: msg.value.delegator_address,
    validator_address: msg.value.validator_address,
  });

  return new google.protobuf.Any({
    type_url: '/cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward',
    value: cosmos.distribution.v1beta1.MsgWithdrawDelegatorReward.encode(message).finish(),
  });
}

export function convertAminoVoteMessageToProto(msg: Msg<MsgVote>) {
  const message = new cosmos.gov.v1beta1.MsgVote({
    proposal_id: msg.value.proposal_id,
    voter: msg.value.voter,
    option: msg.value.option,
  });

  return new google.protobuf.Any({
    type_url: '/cosmos.gov.v1beta1.MsgVote',
    value: cosmos.gov.v1beta1.MsgVote.encode(message).finish(),
  });
}

export function convertAminoSendMessageToProto(msg: Msg<MsgSend>) {
  const message = new cosmos.bank.v1beta1.MsgSend({
    amount: msg.value.amount,
    from_address: msg.value.from_address,
    to_address: msg.value.to_address,
  });

  return new google.protobuf.Any({
    type_url: '/cosmos.bank.v1beta1.MsgSend',
    value: cosmos.bank.v1beta1.MsgSend.encode(message).finish(),
  });
}
